<script setup lang="ts">
const {
  displayFilterBar = true,
  isHorizontalList = false,
  alwaysWrap = false,
  fillContainer = false,
  itemsCount = 16,
} = defineProps<{
  isHorizontalList?: boolean
  alwaysWrap?: boolean
  fillContainer?: boolean
  itemsCount?: number
  displayFilterBar?: boolean
}>()
</script>

<template>
  <div>
    <FilterBarSkeleton v-if="!isHorizontalList && displayFilterBar" />

    <div
      class="loading-list"
      :class="{
        'loading-list--horizontal': isHorizontalList,
        'loading-list--fill': isHorizontalList && fillContainer,
      }"
    >
      <div
        v-for="item in isHorizontalList ? 4 : itemsCount"
        :key="`loading-card-${item}`"
        class="loading-card flex"
        :class="{
          'loading-list--always-wrap': alwaysWrap,
          'loading-card--horizontal': isHorizontalList,
        }"
      >
        <!-- Image skeleton -->
        <div class="loading-card__image loading-effect" />

        <!-- Content skeleton -->
        <div class="loading-card__content">
          <!-- Title skeleton -->
          <div class="loading-card__title loading-effect" />
          <div class="loading-card__title loading-effect" />

          <!-- Information skeleton (rating, tags) -->
          <div class="loading-card__information">
            <div class="loading-card__info-item loading-effect" />
          </div>

          <!-- Price skeleton -->
          <div class="loading-card__price loading-effect" />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="css" scoped>
@import '~/assets/css/loading.css';
@reference '~/assets/css/tailwind.css';

/* ── Filter bar skeleton ── */

.filter-bar-skeleton {
  @apply sticky top-[110px] md:top-(--kft-navbar-height) bg-white;
  @apply pb-1;

  &::before {
    content: '';
    @apply absolute top-0 bottom-0 left-[-9999px] right-[-9999px] z-[-1] bg-white;
  }
}

.filter-chips-row {
  @apply flex gap-2 pt-[11px] pb-3 overflow-x-auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.filter-chip {
  @apply rounded-full flex-shrink-0;
  height: 34px;
  width: 80px;

  @media (min-width: 768px) {
    height: 32px;
  }

  &--wide {
    width: 110px;
  }

  &--narrow {
    width: 60px;
  }
}

.filter-meta-row {
  @apply flex justify-between items-center pb-2;
}

.result-count {
  height: 14px;
  width: 90px;
  border-radius: 4px;
}

.order-by {
  height: 32px;
  width: 100px;
  border-radius: 9999px;
}

/* ── Card grid ── */

.loading-list {
  @apply grid grid-cols-1 gap-2 pt-1;

  &:not(.loading-list--horizontal) {
    @apply md:grid-cols-4 md:gap-4;
  }

  &--horizontal {
    @apply flex flex-row gap-4;

    .loading-card {
      display: none;

      &:nth-child(1),
      &:nth-child(2) {
        display: flex;
      }

      @media (min-width: 768px) {
        display: flex;
      }
    }
  }

  /* Desktop: four cards span the container exactly (three 1rem gaps), matching
     the ProductShowcase slider rail instead of fixed 240px cards. */
  &--fill .loading-card--horizontal {
    @media (min-width: 768px) {
      width: calc((100% - 3rem) / 4);
    }
  }
}

.loading-card {
  @apply bg-white p-1 rounded-xl overflow-hidden;
  height: 146px;
  gap: 0.5rem;

  &:not(.loading-card--horizontal) {
    @apply md:flex-col;

    @media (min-width: 768px) {
      height: 344px;
    }
  }

  &--horizontal {
    height: 204px;
    width: calc(50% - 0.5rem);
    flex-shrink: 0;

    @media (min-width: 768px) {
      flex-direction: column;
      width: 240px;
      height: 344px;
    }
  }

  &__image {
    @apply rounded-lg flex-shrink-0;
    width: 172px;
    height: 136px;

    @media (min-width: 0px) and (max-width: 365px) {
      width: 150px;
    }

    .loading-card:not(.loading-card--horizontal) & {
      @media (min-width: 768px) {
        width: 100%;
        height: 222px;
      }
    }

    .loading-card--horizontal & {
      @media (min-width: 768px) {
        width: 100%;
        height: 222px;
      }
    }
  }

  &__content {
    @apply flex flex-col grow pl-2;
    width: calc(100% - 172px);

    @media (min-width: 0px) and (max-width: 365px) {
      width: calc(100% - 150px);
    }

    .loading-card:not(.loading-card--horizontal) & {
      @media (min-width: 768px) {
        @apply px-1 pt-1.5;
        width: 100%;
      }
    }

    .loading-card--horizontal & {
      @media (min-width: 768px) {
        @apply px-1 pt-1.5;
        width: 100%;
      }
    }
  }

  &__title {
    @apply mb-1;
    height: 16px;
    width: 85%;
    border-radius: 4px;

    @media (min-width: 768px) {
      height: 20px;
    }
  }

  &__information {
    @apply flex gap-1 mb-1 flex-wrap;
    margin-top: 4px;
  }

  &__info-item {
    height: 15px;
    width: 120px;
    border-radius: 4px;
  }

  &__price {
    @apply mt-1;
    height: 20px;
    width: 60px;
    border-radius: 4px;
  }
}
</style>
